import {
	HubConnectionBuilder,
	LogLevel,
	signalMiddleware,
	withCallbacks,
} from 'updated-redux-signalr'
import {
	eventHubService,
	platformService,
	trackingService,
} from '@monthio/react-components'
import {
	createBankSessionSucceeded,
	createBankSessionFailed,
} from './actions/consumerDataPullSessionActions/bankSessionActions'
import {
	BankSession,
	FetchBankSessionsTransactionsResponseDto,
} from '../Bank.types'
import {
	fetchBankSessionsTransactionsFailed,
	fetchBankSessionsTransactionsSucceeded,
} from './actions/consumerDataPullSessionActions/fetchBankSessionsTransactions'
import {
	closeConsumerDataPullSessionFailed,
	closeConsumerDataPullSessionSucceeded,
} from './actions/consumerDataPullSessionActions/closeConsumerDataPullSessionActions'
import { transactionApiClient } from '../../../api/transactionApiClient'
import { notificationService } from '@monthio/react-components'
import i18next from 'i18next'

export const connection = new HubConnectionBuilder()
	.withUrl(
		`${platformService.getAppUrl('transactionApp')}/hubs/bank-sessions`,
		{
			accessTokenFactory: async () => {
				return await transactionApiClient.getTokenWithConsent()
			},
		},
	)
	.configureLogging(LogLevel.Trace)
	.withAutomaticReconnect()
	.build()

let connectionId: string | null = null
export const start = async (): Promise<void> => {
	await connection.start().catch((err) => {
		console.info(
			`[${new Date().toISOString()}] BankSessionHub: Connection error, ${err}.`,
		)
		notificationService.error(i18next.t('websocket-connection-error'))
		throw err
	})

	connectionId = connection.connectionId
	console.info(
		`[${new Date().toISOString()}] BankSessionHub: Connected, connectionId ${connectionId}.`,
	)
}

connection.onclose((err: any) => {
	trackingService.trackError(err, 'onClose', {
		source: 'transactionClientApp',
	})

	notificationService.warning(i18next.t('websocket-disconnected'))
	console.info(
		`[${new Date().toISOString()}] BankSessionHub: Disconnected, connectionId ${
			connection.connectionId
		}.`,
	)
})

connection.onreconnecting(() => {
	trackingService.trackTechnicalEvent(
		'BankSessionsHubUserReconnecting',
		undefined,
		{ source: 'transactionClientApp', treatAsError: true },
	)

	notificationService.info(i18next.t('websocket-reconnecting'))
	console.info(
		`[${new Date().toISOString()}] BankSessionHub: Attempting to reconnect, connectionId ${connectionId}`,
	)
})

connection.onreconnected(() => {
	trackingService.trackTechnicalEvent(
		'BankSessionsHubUserReconnected',
		undefined,
		{ source: 'transactionClientApp', treatAsError: false },
	)

	notificationService.success(i18next.t('websocket-connected'))
	connectionId = connection.connectionId
	console.info(
		`[${new Date().toISOString()}] BankSessionHub: Reconnected successfully, connectionId ${connectionId}`,
	)
})

const callbacks = withCallbacks()
	.add(
		'CreateBankSessionFinished',
		(createdBankSession: BankSession) => (dispatch) => {
			trackingService.trackTechnicalEvent(
				'CreateBankSessionFinished',
				undefined,
				{ source: 'transactionClientApp', treatAsError: false },
			)
			dispatch(createBankSessionSucceeded(createdBankSession))
		},
	)
	.add('CreateBankSessionFailed', (error) => (dispatch) => {
		trackingService.trackTechnicalEvent('CreateBankSessionFailed', undefined, {
			source: 'transactionClientApp',
			treatAsError: true,
		})
		dispatch(createBankSessionFailed(error))
	})
	.add(
		'FetchBankSessionsTransactionsFinished',
		(resp: FetchBankSessionsTransactionsResponseDto) => (dispatch) => {
			trackingService.trackTechnicalEvent(
				'FetchBankSessionsTransactionsFinished',
				undefined,
				{ source: 'transactionClientApp', treatAsError: false },
			)

			if (resp == null) {
				console.error(
					`[${new Date().toISOString()}] FetchBankSessionsTransactionsFinished received null response`,
				)
				dispatch(
					fetchBankSessionsTransactionsFailed({
						name: 'FetchBankSessionsTransactionsFinishedReceivedNullResponse',
						errorText:
							'FetchBankSessionsTransactionsFinished received null response',
						errorCode: '',
						data: undefined,
					}),
				)
				return
			}

			dispatch(
				fetchBankSessionsTransactionsSucceeded(
					resp.clearBudgetAndDebtData,
					resp.banksToAdd,
					resp.transactionDataValidation,
				),
			)
		},
	)
	.add('FetchBankSessionsTransactionsFailed', (apiError) => (dispatch) => {
		trackingService.trackTechnicalEvent(
			'FetchBankSessionsTransactionsFailed',
			apiError,
			{ source: 'transactionClientApp', treatAsError: true },
		)
		dispatch(fetchBankSessionsTransactionsFailed(apiError))
	})
	.add('CloseConsumerDataPullSessionFinished', () => (dispatch) => {
		trackingService.trackTechnicalEvent('CloseConsumerDataPullSessionFinished')
		dispatch(closeConsumerDataPullSessionSucceeded())
	})
	.add('CloseConsumerDataPullSessionFailed', (apiError) => (dispatch) => {
		trackingService.trackTechnicalEvent(
			'CloseConsumerDataPullSessionFailed',
			apiError,
			{ source: 'transactionClientApp', treatAsError: true },
		)
		dispatch(closeConsumerDataPullSessionFailed(apiError))
	})
	.add('CreateBankSessionProgressLevel', (input: number) => () => {
		eventHubService.onEvent({
			payload: input,
			topic: 'CreateBankSessionProgressLevel',
			source: 'transactionApp/hubs/bank-sessions',
		})
	})

export const signal = signalMiddleware({
	callbacks,
	connection,
	shouldConnectionStartImmediately: false,
})
